// app/src/lib/auth/jwt.js — verify Cognito ID + access tokens against the
// User Pool's JWKS. **Never trust unverified JWTs.**
//
// Cache strategy: JWKS fetched once per session and cached in memory; on `kid` miss
// (key rotation) the cache is invalidated and re-fetched at most once per minute.

import { b64uDecode, timingSafeEqual } from '../crypto.js';

/** @type {{ keys: Map<string, CryptoKey>, fetchedAt: number } | null} */
let jwksCache = null;
let lastRefreshAt = 0;

const enc = new TextEncoder();

/**
 * @param {string} jwt
 * @param {string} jwksUrl       - https://cognito-idp.<region>.amazonaws.com/<userPoolId>/.well-known/jwks.json
 * @param {string} expectedIssuer
 * @param {string} expectedAudience
 * @returns {Promise<object>} the verified claims.
 */
export async function verifyJwt(jwt, jwksUrl, expectedIssuer, expectedAudience) {
  const parts = jwt.split('.');
  if (parts.length !== 3) throw new Error('jwt: malformed');
  const headerJson = JSON.parse(new TextDecoder().decode(b64uDecode(parts[0])));
  const payloadJson = JSON.parse(new TextDecoder().decode(b64uDecode(parts[1])));
  if (headerJson.alg !== 'RS256') throw new Error(`jwt: unexpected alg ${headerJson.alg}`);
  if (!headerJson.kid) throw new Error('jwt: missing kid');

  const key = await resolveKey(headerJson.kid, jwksUrl);
  const signedInput = enc.encode(parts[0] + '.' + parts[1]);
  const sig = b64uDecode(parts[2]);
  const ok = await crypto.subtle.verify(
    { name: 'RSASSA-PKCS1-v1_5' },
    key,
    sig,
    signedInput,
  );
  if (!ok) throw new Error('jwt: signature invalid');

  const now = Math.floor(Date.now() / 1000);
  if (typeof payloadJson.exp !== 'number' || payloadJson.exp < now) throw new Error('jwt: expired');
  if (typeof payloadJson.nbf === 'number' && payloadJson.nbf > now + 60) throw new Error('jwt: not yet valid');
  if (payloadJson.iss !== expectedIssuer) throw new Error('jwt: bad issuer');

  // Cognito puts the client ID in `aud` for ID tokens, `client_id` for access tokens.
  const aud = payloadJson.aud ?? payloadJson.client_id;
  if (aud !== expectedAudience) throw new Error('jwt: bad audience');

  return payloadJson;
}

async function resolveKey(kid, jwksUrl) {
  if (jwksCache?.keys.has(kid)) return jwksCache.keys.get(kid);
  // Throttle JWKS refresh to avoid pathological key-rotation loops.
  const now = Date.now();
  if (now - lastRefreshAt < 60_000 && jwksCache) {
    throw new Error('jwt: unknown kid');
  }
  lastRefreshAt = now;

  const res = await fetch(jwksUrl, { credentials: 'omit', cache: 'no-store' });
  if (!res.ok) throw new Error('jwks: fetch failed');
  const { keys } = await res.json();
  const map = new Map();
  for (const k of keys) {
    if (k.kty !== 'RSA' || k.alg !== 'RS256') continue;
    const ck = await crypto.subtle.importKey(
      'jwk',
      k,
      { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
      false,
      ['verify'],
    );
    map.set(k.kid, ck);
  }
  jwksCache = { keys: map, fetchedAt: now };
  const found = map.get(kid);
  if (!found) throw new Error('jwt: kid not in JWKS');
  return found;
}

/** Decode payload WITHOUT verification — only for inspecting our own freshly-issued tokens to surface UI hints. */
export function unsafeDecodePayload(jwt) {
  const parts = jwt.split('.');
  if (parts.length !== 3) throw new Error('jwt: malformed');
  return JSON.parse(new TextDecoder().decode(b64uDecode(parts[1])));
}
